/*
 * QUEST SOFTWARE PROPRIETARY INFORMATION
 *
 * This software is confidential.  Quest Software Inc., or one of its
 * subsidiaries, has supplied this software to you under terms of a
 * license agreement, nondisclosure agreement or both.
 *
 * You may not copy, disclose, or use this software except in accordance with
 * those terms.
 *
 *
 * Copyright 2026 Quest Software Inc.
 * ALL RIGHTS RESERVED.
 *
 * QUEST SOFTWARE INC. MAKES NO REPRESENTATIONS OR
 * WARRANTIES ABOUT THE SUITABILITY OF THE SOFTWARE,
 * EITHER EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED
 * TO THE IMPLIED WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE, OR
 * NON-INFRINGEMENT.  QUEST SOFTWARE SHALL NOT BE
 * LIABLE FOR ANY DAMAGES SUFFERED BY LICENSEE
 * AS A RESULT OF USING, MODIFYING OR DISTRIBUTING
 * THIS SOFTWARE OR ITS DERIVATIVES.
 */

import { createFeatureSelector, createSelector, State } from '@ngrx/store';
import {
  AlarmTemplateState,
  DomainObjectCountState,
  DomainRuleState,
  AssignedNotificationsState,
} from './alarm-template.state';
import { AlarmsFeatureState } from './../alarm-store-index';

export const selectAlarmTemplatesStateSelector =
  createFeatureSelector<AlarmsFeatureState>('alarmTemplates');

export const selectAlarmTemplateState = createSelector(
  selectAlarmTemplatesStateSelector,
  (state: AlarmsFeatureState) => state?.alarmTemplateState,
);

export const selectAlarmTemplateData = createSelector(
  selectAlarmTemplateState,
  (state: AlarmTemplateState) => state?.alarmTemplateData,
);

export const selectIsLoading = createSelector(
  selectAlarmTemplateState,
  (state: AlarmTemplateState) => state?.isLoading,
);
export const selectIsError = createSelector(
  selectAlarmTemplateState,
  (state: AlarmTemplateState) => state?.error,
);

export const selectIsEmpty = createSelector(
  selectAlarmTemplateState,
  (state: AlarmTemplateState) => state?.isEmpty,
);

export const selectCurrentAlarmTemplate = createSelector(
  selectAlarmTemplateState,
  (state: AlarmTemplateState) => state?.currentAlarmTemplate,
);

export const selectAlarmTemplateStateRuleCount = createSelector(
  selectAlarmTemplatesStateSelector,
  (state: AlarmsFeatureState) => state?.domainRuleState,
);

export const selectDomainRuleCounts = createSelector(
  selectAlarmTemplateStateRuleCount,
  (state: DomainRuleState) => state.domainRuleCounts, // Ensure you're accessing the right state property
);

export const selectAlarmTemplateStateNotificationChannel = createSelector(
  selectAlarmTemplatesStateSelector,
  (state: AlarmsFeatureState) => state?.notificationChannelState,
);

export const selectNotificationChannels = createSelector(
  selectAlarmTemplateStateNotificationChannel,
  (state) => state.notificationChannels,
);

export const selectNotificationChannelsLoading = createSelector(
  selectAlarmTemplateStateNotificationChannel,
  (state) => state.loading,
);

export const selectNotificationChannelsError = createSelector(
  selectAlarmTemplateStateNotificationChannel,
  (state) => state.error,
);

export const selectDomainObjectCountState = createSelector(
  selectAlarmTemplatesStateSelector,
  (state: AlarmsFeatureState) => state?.domainObjectCountState,
);

// Selector to get domain counts
export const selectDomainObjectCounts = createSelector(
  selectDomainObjectCountState,
  (state: DomainObjectCountState) => state.domainCounts,
);

// Selector to check loading state
export const selectDomainObjectCountsLoading = createSelector(
  selectDomainObjectCountState,
  (state: DomainObjectCountState) => state.loading,
);

// Selector to get error state
export const selectDomainObjectCountsError = createSelector(
  selectDomainObjectCountState,
  (state: DomainObjectCountState) => state.error,
);

export const selectAssignedNotificationsState = createSelector(
  selectAlarmTemplatesStateSelector,
  (state: AlarmsFeatureState) => state?.assignedNotificationsState,
);

export const selectListOfAssignedNotifications = createSelector(
  selectAssignedNotificationsState,
  (state: AssignedNotificationsState) => state?.listOfAssignedNotifications,
);

export const selectAssignedNotificationsLoading = createSelector(
  selectAssignedNotificationsState,
  (state: AssignedNotificationsState) => state?.loading,
);

export const selectAssignedNotificationsError = createSelector(
  selectAssignedNotificationsState,
  (state: AssignedNotificationsState) => state?.error,
);
